import { assoc } from 'ramda';
import { SelectOptionMetaType } from '#coreComponents/Select/types';
import { ROOT_PARENT_ID } from '#coreComponents/Select/Context';

import {
  QUERY_SELECT_ONE_CHANGE,
  QUERY_SELECT_THREE_CHANGE,
  QUERY_SELECT_TWO_CHANGE,
  YEAR_CHANGE,
} from './constants';
import { Action, State } from './types';
import { getQuerySelectOptions, getFilterSelectOptions } from './utils';

const initialState = {
  isMapStatic: false,
  selectedYears: [],
  querySelectOne: {
    label: 'Indicator',
    data: {
      options: [],
      meta: { type: 'radio' as SelectOptionMetaType },
    },
    selectedItems: [],
  },
  querySelectTwo: {
    label: 'Dimension',
    data: {
      options: [],
      meta: { type: 'radio' as SelectOptionMetaType },
    },
    selectedItems: [],
  },
  querySelectThree: {
    label: 'Group',
    data: {
      options: [],
      meta: { type: 'radio' as SelectOptionMetaType },
    },
    selectedItems: [],
  },
  variableOptions: [],
  map: [],
  filteredOptions: {},
  filterSelectOptions: [],
  selectedFilters: {},
};

const reducer: React.Reducer<State, Action> = (
  state = initialState,
  action,
) => {
  const {
    querySelectOne: { data: querySelectOneData },
    querySelectTwo,
    querySelectTwo: {
      data: { meta: selectTwoMeta },
    },
    querySelectThree,
    querySelectThree: {
      data: { meta: selectThreeMeta },
    },
    map,
    variableOptions,
    filteredOptions,
    selectedFilters,
  } = state;

  switch (action.type) {
    case YEAR_CHANGE:
      return assoc('selectedYears', action.payload, state);

    case QUERY_SELECT_ONE_CHANGE: {
      const { payload } = action;
      if (!payload.length) {
        return state;
      }

      const [{ selectedItems, groupId }] = payload;
      const updatedLabelKey =
        groupId === ROOT_PARENT_ID ? selectedItems[0] : groupId;
      const selectedOption = querySelectOneData.options.find(
        (option) => option.key === updatedLabelKey,
      );

      const filterSelectOptions = getFilterSelectOptions(
        map,
        updatedLabelKey,
        filteredOptions,
        selectedFilters,
      );

      const getOptions = getQuerySelectOptions.bind({
        map,
        variableOptions,
        selectedKey: updatedLabelKey,
      });

      const querySelectTwoDataOptions = getOptions('querySelectTwo');
      const querySelectTwoSelectedOptionKey =
        (querySelectTwoDataOptions.find(
          ({ key }) => key === selectTwoMeta?.selected,
        ) &&
          selectTwoMeta?.selected) ||
        querySelectTwoDataOptions[0].key;
      const querySelectTwoData = {
        options: querySelectTwoDataOptions,
        meta: {
          type: 'radio' as SelectOptionMetaType,
          selected: querySelectTwoSelectedOptionKey,
        },
      };

      const querySelectThreeDataOptions = getOptions('querySelectThree');
      const querySelectThreeFilteredDataOptions = querySelectThreeDataOptions.filter(
        (option) => option.key !== querySelectTwoSelectedOptionKey,
      );

      const querySelectThreeSelectedOptionKey =
        selectThreeMeta?.selected ||
        querySelectThreeFilteredDataOptions[0]?.key;
      const querySelectThreeData = {
        options: querySelectThreeFilteredDataOptions,
        meta: {
          type: 'radio' as SelectOptionMetaType,
          selected: querySelectThreeSelectedOptionKey,
        },
      };

      const selectedMap = map.find((m) => m.key === updatedLabelKey);

      return {
        ...state,
        filterSelectOptions,
        isMapStatic: selectedMap?.type === 'static',
        querySelectOne: {
          label: selectedOption?.label || 'Indicator',
          data: querySelectOneData,
          selectedItems,
        },
        querySelectTwo: {
          ...querySelectTwo,
          label: querySelectTwoData.options[0]?.label || 'Dimension',
          data: querySelectTwoData || {
            options: querySelectTwoDataOptions,
            meta: {
              type: 'radio' as SelectOptionMetaType,
              selected: querySelectTwoSelectedOptionKey,
            },
          },
        },
        querySelectThree: {
          ...querySelectThree,
          label: querySelectThreeData.options[0]?.label || 'Group',
          data: querySelectThreeData,
        },
      };
    }

    case QUERY_SELECT_TWO_CHANGE: {
      const { payload } = action;
      if (!payload.length) {
        return state;
      }

      const [{ selectedItems }] = payload;
      const updatedLabelKey = selectedItems[0];
      const { isMapStatic } = state;

      const querySelectTwoSelectedOption = querySelectTwo.data.options.find(
        (option) => option.key === updatedLabelKey,
      );

      const querySelectThreeStaticOptions = querySelectThree.data.options;
      const querySelectThreeDynamicOptions = querySelectTwo.data.options.filter(
        (option) => option.key !== updatedLabelKey,
      );
      const querySelectThreeUpdatedOptions = isMapStatic
        ? querySelectThreeStaticOptions
        : querySelectThreeDynamicOptions;

      const querySelectThreeSelectedOptionKey =
        (querySelectThreeUpdatedOptions.find(
          ({ key }) => key === selectThreeMeta?.selected,
        ) &&
          selectThreeMeta?.selected) ||
        querySelectThreeUpdatedOptions[0]?.key;

      return {
        ...state,
        querySelectTwo: {
          ...querySelectTwo,
          label: querySelectTwoSelectedOption?.label || 'Dimension',
          selectedItems,
        },
        querySelectThree: {
          ...querySelectThree,
          label: querySelectThreeUpdatedOptions[0]?.label,
          data: {
            options: querySelectThreeUpdatedOptions,
            meta: {
              ...querySelectThree.data.meta,
              selected: querySelectThreeSelectedOptionKey,
            },
          },
        },
      };
    }

    case QUERY_SELECT_THREE_CHANGE: {
      const { payload } = action;
      if (!payload.length) {
        return state;
      }

      const [{ selectedItems }] = payload;
      const updatedLabelKey = selectedItems[0];
      const updatedLabel = querySelectThree.data.options.find(
        (option) => option.key === updatedLabelKey,
      )?.label;

      return {
        ...state,
        querySelectThree: {
          ...querySelectThree,
          selectedItems,
          label: updatedLabel || 'Group',
        },
      };
    }

    default:
      return state;
  }
};

export { reducer, initialState };
